<template>
  <section class="w-full bg-gradient-to-b from-primary-50 to-primary-100" id="why-us">
    <div class="mx-auto flex max-w-7xl flex-col gap-12 px-4 py-16 sm:px-6 lg:gap-16 lg:px-8 lg:py-24">
      <!-- Split: image + copy -->
      <div class="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
        <!-- Image -->
        <div class="relative">
          <div class="relative overflow-hidden rounded-2xl shadow-[0px_20px_48px_-12px_rgba(127,86,217,0.3)]">
            <img
              :src="AppScreenshot"
              alt="Designer Tool editor showing a postcard being designed"
              class="w-full object-cover"
            />
            <!-- "Printed as designed" chip -->
            <div
              class="absolute bottom-4 left-4 flex items-center gap-2 rounded-full bg-white px-3 py-1.5 shadow-[0px_4px_12px_-2px_rgba(16,24,40,0.18)]"
            >
              <svg
                class="size-3.5 text-success-600"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12l5 5L20 7" />
              </svg>
              <span class="text-xs font-semibold text-gray-950">Printed as designed</span>
            </div>
          </div>
        </div>

        <!-- Copy -->
        <div class="flex flex-col gap-6">
          <span class="text-sm font-semibold uppercase tracking-wider text-primary-600">
            Why platforms embed Designer Tool
          </span>
          <h2 class="text-pretty text-3xl font-semibold tracking-[-0.4px] text-gray-950 sm:text-4xl sm:tracking-[-0.72px] lg:text-balance">
            Design inside your product. Print anywhere.
          </h2>
          <p class="text-lg text-gray-700">
            Three things your users get on day one, without your team building an editor.
          </p>
        </div>
      </div>

      <!-- 3 feature cards -->
      <div class="grid grid-cols-1 gap-6 sm:grid-cols-3">
        <div
          v-for="feature in features"
          :key="feature.title"
          class="relative flex flex-col gap-4 rounded-2xl border border-primary-100 bg-white p-6 shadow-[0px_4px_6px_-2px_rgba(127,86,217,0.06)]"
        >
          <div
            class="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0px_-1px_0px_0px_rgba(233,215,254,0.4)]"
            aria-hidden="true"
          />
          <div
            class="flex size-12 items-center justify-center rounded-[10px] border border-primary-100 bg-gradient-to-b from-white to-primary-50 p-2.5 shadow-[0px_2px_0px_0px_rgba(16,24,40,0.08)]"
          >
            <svg
              class="size-6 text-primary-600"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
              v-html="feature.iconPath"
            />
          </div>
          <h3 class="text-lg font-semibold text-gray-950">{{ feature.title }}</h3>
          <p class="text-sm text-gray-700">{{ feature.description }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import AppScreenshot from "@/../images/app-screenshot.png";

const features = [
  {
    title: "Embeds in an afternoon",
    description:
      "One script tag or an iframe. Theme it to your brand, pass a user token, listen for the export event. Works on your domain.",
    iconPath: `<path d="M8 6l-6 6 6 6M16 6l6 6-6 6"/>`,
  },
  {
    title: "HTML that prints",
    description:
      "Bleed, safe zones and 300 DPI images are enforced in the editor, so every export passes Lob and PostGrid preflight the first time.",
    iconPath: `<path d="M5 12l5 5L20 7"/>`,
  },
  {
    title: "Variable data built in",
    description:
      "Merge fields, per-recipient preview and CSV upload without a plugin. Fields map straight to your mail API's merge variables.",
    iconPath: `<path d="M8 4H6a2 2 0 0 0-2 2v4a2 2 0 0 1-2 2 2 2 0 0 1 2 2v4a2 2 0 0 0 2 2h2M16 4h2a2 2 0 0 1 2 2v4a2 2 0 0 0 2 2 2 2 0 0 0-2 2v4a2 2 0 0 1-2 2h-2"/>`,
  },
];
</script>
